import { ReactNode, useState } from "react";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { NavBar } from "../../UI/Layout/NavBar.tsx";
import { Footer } from "../../UI/Layout/Footer.tsx";
import { AdminMainContainer } from "../../UI/Admin/AdminMainContainer.tsx";
import { ErrorState } from "../../UI/ErrorState.tsx";
import { Pagination } from "../../UI/Pagination.tsx";
import { DebouncedSearchInput } from "../../UI/DebouncedSearchInput.tsx";
import { hasActiveListFilters } from "../../UI/Table/listUrlParams.ts";
import { OrdersListQuery, pickOrdersFilters } from "./OrdersListQuery.ts";
import { ExportCsvButton } from "../../UI/ExportCsvButton.tsx";
import { ExportOrdersCsvModal } from "./ExportOrdersCsvModal.tsx";
import { SalesGateway } from "../SalesStatistics/SaleRecord.ts";
import { SalesStatisticsButton } from "../SalesStatistics/SalesStatisticsButton.tsx";

export type OrdersCsvExport = {
  allOrdersDescription: string;
  download: () => Promise<void>;
};

export function OrdersListPage<Status extends string>({
  title,
  searchPlaceholder,
  query,
  total,
  hasError,
  onSearch,
  onClearFilters,
  onChangePage,
  csvExport,
  statisticsGateway,
  children,
}: {
  title: string;
  searchPlaceholder: string;
  query: OrdersListQuery<Status>;
  total: number;
  hasError: boolean;
  onSearch: (search: string) => void;
  onClearFilters: () => void;
  onChangePage: (pageIndex: number) => void;
  csvExport?: OrdersCsvExport;
  statisticsGateway?: SalesGateway;
  children: ReactNode;
}) {
  const [isExporting, setIsExporting] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / query.pageSize));
  const hasFilters = hasActiveListFilters(pickOrdersFilters(query));

  return (
    <Layout>
      <AdminMainContainer>
        <NavBar showAdminSideBarHandle />
        <div className="flex-1 p-4 flex flex-col gap-4 max-w-full">
          <div className="flex items-center gap-3">
            <h1 className="font-bold text-3xl flex-1">{title}</h1>
            {statisticsGateway && (
              <SalesStatisticsButton gateway={statisticsGateway} />
            )}
            {csvExport && (
              <ExportCsvButton onClick={() => setIsExporting(true)} />
            )}
          </div>
          <DebouncedSearchInput
            placeholder={searchPlaceholder}
            value={query.search ?? ""}
            onSearch={onSearch}
          />
          {hasError ? (
            <ErrorState />
          ) : (
            <>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span>{total} ordini trovati</span>
                {hasFilters && (
                  <button
                    type="button"
                    onClick={onClearFilters}
                    className="text-blue-600 hover:underline"
                  >
                    Rimuovi tutti i filtri
                  </button>
                )}
              </div>
              {children}
              <Pagination
                currentPage={query.page}
                totalPages={totalPages}
                onChangePage={onChangePage}
              />
            </>
          )}
        </div>
      </AdminMainContainer>
      <Footer />
      {csvExport && isExporting && (
        <ExportOrdersCsvModal
          totalCount={total}
          search={query.search}
          hasFilters={hasFilters}
          allOrdersDescription={csvExport.allOrdersDescription}
          onExport={csvExport.download}
          onClose={() => setIsExporting(false)}
        />
      )}
    </Layout>
  );
}
